/* Grid layout per user for EVERY DataGrid of the app (installed once in index.js):
 *   - columns can be dragged into another order, resized and shown / hidden (Columns button);
 *   - what the user changes is saved on the server (user-grid-layout) per user, page and grid, and applied when the grid
 *     opens again - on any PC; the Reset button (↺) brings back the default columns;
 *   - when a release changes a grid's columns, the user's layout is carried over (gridLayout.js findLayout).
 * Only changes made by the user inside the grid are saved (a click / drag in the grid or its Columns list shortly before),
 * not columns a screen shows / hides by itself. Grids with their own stateStoring and banded grids are left alone. */
import DataGrid from 'devextreme/ui/data_grid';
import { axiosGet, axiosPost } from '../apis/useAxios';
import { applyLayout, columnIds, columnRef, emptyLayout, findLayout, gridKey, isEmptyLayout, layoutSettings, orderFrom, parseLayout, resetMarker } from './gridLayout';

const USER_WINDOW_MS = 3000;
const SAVE_DELAY_MS = 800;
const CHANGE = /^columns\[(\d+)\]\.(visibleIndex|width|visible)$/;

const store = { userId: 0, layouts: new Map(), loaded: false, waiting: new Set() };
let lastGesture = { at: 0, target: null };

const currentUserId = () => {
  try { return Number(sessionStorage.getItem('session_userid')) || 0; } catch { return 0; }
};
const query = () => `user-grid-layout?userId=${store.userId}`;
const alive = (grid) => {
  try { const el = grid.element(); return !!el && document.body.contains(el); } catch { return false; }
};

/** Sets columns from a layout on a grid that is already shown (layout loaded late, or Reset). */
function setColumns(grid, t, settings) {
  t.quiet = true;
  try {
    grid.beginUpdate();
    t.ids.forEach((id, i) => {
      if (t.refs[i]) grid.columnOption(t.refs[i], settings[i]);
    });
    grid.endUpdate();
  } finally {
    t.quiet = false;
  }
}

function loadLayouts() {
  const uid = currentUserId();
  if (!uid || uid === store.userId) return;
  store.userId = uid;
  store.layouts = new Map();
  store.loaded = false;
  axiosGet(query())
    .then((r) => (r?.payload || []).forEach((row) => {
      const l = parseLayout(row.layoutJson ?? row.LayoutJson);
      if (l) store.layouts.set(row.gridKey ?? row.GridKey, l);
    }))
    .catch(() => {})
    .finally(() => {
      store.loaded = true;
      store.waiting.forEach(({ grid, t }) => {
        const l = !t.changed && alive(grid) ? findLayout(store.layouts, t.key, t.ids) : null;
        if (l) {
          t.layout = l;
          setColumns(grid, t, layoutSettings(t.ids, l));
        }
      });
      store.waiting.clear();
    });
}

function saveSoon(grid, t) {
  clearTimeout(t.timer);
  t.timer = setTimeout(() => {
    if (t.orderChanged) {
      t.layout.order = orderFrom(t.ids, t.refs.map((ref) => (ref ? grid.columnOption(ref, 'visibleIndex') : undefined)));
      t.orderChanged = false;
    }
    if (isEmptyLayout(t.layout)) return;
    t.layout.ids = t.ids;
    store.layouts.set(t.key, t.layout);
    axiosPost(query(), { gridKey: t.key, layoutJson: JSON.stringify(t.layout) }).catch(() => {});
  }, SAVE_DELAY_MS);
}

function resetLayout(grid, t) {
  clearTimeout(t.timer);
  const marker = resetMarker(t.ids);
  store.layouts.set(t.key, marker);
  t.layout = emptyLayout();
  t.orderChanged = false;
  setColumns(grid, t, t.defaults);
  axiosPost(query(), { gridKey: t.key, layoutJson: JSON.stringify(marker) }).catch(() => {});
}

/* the change follows a click / drag / key in this grid or in a popup (its Columns list) */
function madeByUser(grid) {
  if (Date.now() - lastGesture.at > USER_WINDOW_MS || !lastGesture.target) return false;
  try {
    const el = grid.element();
    return el.contains(lastGesture.target) || !!lastGesture.target.closest?.('.dx-overlay-wrapper, .dx-datagrid-column-chooser');
  } catch {
    return false;
  }
}

function onOptionChanged(grid, t, e) {
  const m = CHANGE.exec(e.fullName || '');
  if (!m || t.quiet || !madeByUser(grid)) return;
  const id = t.ids[Number(m[1])];
  if (id === undefined) return;
  t.changed = true;
  if (m[2] === 'width') t.layout.widths[id] = e.value;
  else if (m[2] === 'visible') t.layout.hidden[id] = e.value === false;
  else t.orderChanged = true;
  saveSoon(grid, t);
}

/* Reset (↺), and a Columns button where the screen lists its own toolbar items without the grid's one */
function addResetButton(grid, t, e) {
  const items = e.toolbarOptions?.items;
  if (!Array.isArray(items) || items.some((x) => x?.name === 'resetLayout')) return;
  if (!items.some((x) => x?.name === 'columnChooserButton' || x?.name === 'columnsLayout')) {
    items.push({
      location: 'after', widget: 'dxButton', name: 'columnsLayout', locateInMenu: 'auto',
      options: { icon: 'column-chooser', hint: 'Columns: show / hide', onClick: () => grid.showColumnChooser() },
    });
  }
  items.push({
    location: 'after', widget: 'dxButton', name: 'resetLayout', locateInMenu: 'auto',
    options: { icon: 'revert', hint: 'Reset columns (order, width, shown / hidden) to the default', onClick: () => resetLayout(grid, t) },
  });
}

/* before the grid builds its columns: the saved layout goes straight into the column configuration (no flicker) */
function prepare(grid) {
  if (grid.option('stateStoring.enabled')) return null;
  const columns = grid.option('columns');
  if (!Array.isArray(columns) || !columns.length || columns.some((c) => c && Array.isArray(c.columns))) return null;
  loadLayouts();
  if (!store.userId) return null;
  const ids = columnIds(columns);
  const t = {
    key: gridKey(window.location.pathname, ids), ids, refs: columns.map(columnRef), layout: emptyLayout(),
    defaults: columns.map((c, i) => ({ visibleIndex: c?.visibleIndex ?? i, width: c?.width, visible: c?.visible !== false })),
  };
  const saved = findLayout(store.layouts, t.key, t.ids);
  if (saved) {
    t.layout = JSON.parse(JSON.stringify(saved));
    grid._options.silent('columns', applyLayout(columns, ids, saved)); // eslint-disable-line no-underscore-dangle
  } else if (!store.loaded) {
    store.waiting.add({ grid, t });
  }
  return t;
}

let installed = false;

export default function installGridLayouts() {
  if (installed) return;
  installed = true;
  DataGrid.defaultOptions({
    options: { allowColumnReordering: true, allowColumnResizing: true, columnChooser: { enabled: true, mode: 'select', title: 'Columns' } },
  });
  const remember = (ev) => { lastGesture = { at: Date.now(), target: ev.target }; };
  ['pointerdown', 'pointerup', 'keydown'].forEach((name) => document.addEventListener(name, remember, true));

  const originalInit = DataGrid.prototype._init; // eslint-disable-line no-underscore-dangle
  // eslint-disable-next-line no-underscore-dangle, func-names
  DataGrid.prototype._init = function (...args) {
    let t = null;
    try { t = prepare(this); } catch { t = null; }
    const result = originalInit.apply(this, args);
    if (t) {
      this.on('optionChanged', (e) => onOptionChanged(this, t, e));
      this.on('toolbarPreparing', (e) => addResetButton(this, t, e));
    }
    return result;
  };
}
